import { createContext, useContext, useState, useCallback, useEffect, useRef, ReactNode } from 'react';
import { Message, MCPServerConfig } from '../types';
import { sendChatMessageSplitPhase } from '../services/aiService';
import { runBootSequence } from '../hooks/useBootSequence';
import { DATABASE_SERVICE_URL } from '../services/workerUrls';
import { logService } from '../services/logService';
import { notificationService } from '../services/notificationService';

interface ChatContextState {
  messages: Message[];
  isLoading: boolean;
  error: string | null;
  sendMessage: (content: string) => Promise<void>;
  clearMessages: () => void;
}

const ChatContext = createContext<ChatContextState | null>(null);

interface ChatProviderProps {
  children: ReactNode;
  playerId: string;
  mcpServers?: MCPServerConfig[];
}

// Chat persistence API
async function loadChatHistory(playerId: string): Promise<Message[]> {
  const logId = logService.api('GET', `/api/chat/${playerId}`, {}, 'pending');
  const startTime = Date.now();

  try {
    const response = await fetch(`${DATABASE_SERVICE_URL()}/api/chat/${playerId}`);
    const duration = Date.now() - startTime;

    if (!response.ok) {
      logService.updateLog(logId, { status: 'error', duration, message: `HTTP ${response.status}` });
      return [];
    }

    const result = await response.json();
    if (!result.success || !result.data) {
      logService.updateLog(logId, { status: 'error', duration, message: 'No data returned' });
      return [];
    }

    logService.updateLog(logId, { status: 'success', duration, details: { messageCount: result.data.length } });

    // Convert database messages to UI messages
    return result.data.map((m: { id: string; role: 'user' | 'assistant'; content: string; tool_calls: string | null; created_at: string }) => ({
      id: m.id,
      role: m.role,
      content: m.content || '[Message content unavailable]',
      toolCalls: m.tool_calls ? JSON.parse(m.tool_calls) : undefined,
      timestamp: new Date(m.created_at),
    }));
  } catch (err) {
    const duration = Date.now() - startTime;
    logService.updateLog(logId, { status: 'error', duration, message: err instanceof Error ? err.message : 'Unknown error' });
    return [];
  }
}

// Note: Message persistence removed from frontend - handled by AI service backend
// Messages are saved when the AI service processes them, ensuring atomicity

async function clearChatHistory(playerId: string): Promise<void> {
  const logId = logService.db('DELETE', 'chat_messages', { playerId }, 'pending');
  const startTime = Date.now();

  try {
    const response = await fetch(`${DATABASE_SERVICE_URL()}/api/chat/${playerId}`, {
      method: 'DELETE',
    });
    const duration = Date.now() - startTime;

    if (response.ok) {
      logService.updateLog(logId, { status: 'success', duration });
    } else {
      logService.updateLog(logId, { status: 'error', duration, message: `HTTP ${response.status}` });
    }
  } catch (err) {
    const duration = Date.now() - startTime;
    logService.updateLog(logId, { status: 'error', duration, message: err instanceof Error ? err.message : 'Unknown error' });
  }
}

export function ChatProvider({ children, playerId, mcpServers }: ChatProviderProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const bootRanRef = useRef(false);
  const messagesRef = useRef<Message[]>([]);
  const historyLoadedRef = useRef(false);

  // Keep messagesRef in sync with messages state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  // Load chat history and run boot sequence on mount
  useEffect(() => {
    if (bootRanRef.current) return;
    bootRanRef.current = true;

    async function initialize() {
      setIsLoading(true);

      // First, load persisted chat history
      if (!historyLoadedRef.current) {
        historyLoadedRef.current = true;
        const history = await loadChatHistory(playerId);

        if (history.length > 0) {
          // We have history - restore it and skip boot sequence
          setMessages(history);
          setIsLoading(false);
          return;
        }
      }

      // No history - run boot sequence for new session
      const updateBootMessage = (content: string) => {
        setMessages([{
          id: 'boot-sequence',
          role: 'assistant',
          content: '```\n' + content + '\n```',
          timestamp: new Date(),
        }]);
      };

      try {
        await runBootSequence(updateBootMessage, playerId);
      } catch (err) {
        console.error('Boot sequence failed:', err);
        updateBootMessage('Boot sequence failed. Systems may be offline.\nship:~$ Ready for input...');
      } finally {
        setIsLoading(false);
      }
    }

    initialize();
  }, [playerId]);

  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return;

    const userMessage: Message = {
      id: crypto.randomUUID(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);
    setError(null);

    // Progress message ID for live updates
    const progressMessageId = crypto.randomUUID();
    let progressLines: string[] = [];

    try {
      // Create initial progress message
      const progressMessage: Message = {
        id: progressMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, progressMessage]);

      // Comedic progress messages (Red Dwarf / Hitchhiker's Guide style)
      const comedicMessages: Record<number, string> = {
        1: "🤖 **Phase 1: Intent Classification** — The ship's semi-sentient AI is attempting to understand what you just said. This could take a while...",
        2: "📊 **Phase 2: Game State Loading** — Loading game state from the improbability drive. Don't panic.",
        3: "⚙️ **Phase 3: RAG Query** — Consulting the deep thought mainframe to resolve your request. The answer might be 42.",
        4: "🎭 **Phase 4: Narrative Extension** — The comedy writer is planning absurd complications. Stand by for narrative excellence.",
        5: "🏭 **Phase 5: Entity Creator** — Manufacturing NPCs with unfortunate names and questionable life choices...",
        6: "⚡ **Phase 6: Action Execution** — Executing your plan. What could possibly go wrong?",
        7: "✍️ **Phase 7: Narrator** — The AI is crafting a suitably dramatic response. Dignity optional.",
        8: "💾 **Phase 8: Persistence** — Recording this moment in the annals of cosmic insignificance...",
      };

      // Call split-phase orchestrator with progress updates
      const result = await sendChatMessageSplitPhase(content, {
        playerId,
        mcpServers,
        onProgress: (phase: number, description: string, complete: boolean) => {
          if (!complete) {
            // Starting phase - show comedic message
            progressLines[phase - 1] = comedicMessages[phase] || `Phase ${phase}: ${description}...`;
          } else {
            // Completed phase - update with checkmark
            progressLines[phase - 1] = `Phase ${phase}: ${description}... ✓`;
          }

          // Update progress message with current lines (with spacing)
          setMessages(prev =>
            prev.map(msg =>
              msg.id === progressMessageId
                ? { ...msg, content: progressLines.join('\n\n') }
                : msg
            )
          );
        },
      });

      // Keep progress message visible and add final response below it
      const assistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: result.response || '[No response received]',
        toolCalls: result.toolCalls,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, assistantMessage]);

      // Show notification that response is ready
      notificationService.showResponseNotification(
        'Space Antics',
        'Your response is ready'
      );
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to send message';
      setError(errorMessage);

      // Remove progress message and add error message
      setMessages(prev => prev.filter(msg => msg.id !== progressMessageId));

      const errorAssistantMessage: Message = {
        id: crypto.randomUUID(),
        role: 'assistant',
        content: `Sorry, I encountered an error: ${errorMessage}`,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorAssistantMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [playerId, mcpServers]);

  const clearMessages = useCallback(() => {
    setMessages([]);
    setError(null);
    clearChatHistory(playerId);
  }, [playerId]);

  return (
    <ChatContext.Provider value={{ messages, isLoading, error, sendMessage, clearMessages }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return context;
}
